import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Calendar,
  Clock,
  CalendarCheck,
  Waves,
  Users,
  BarChart2,
  Bell,
  LogOut,
  Timer,
  LayoutList,
  User,
  ShieldAlert,
  ClipboardList,
  ChevronDown,
  FileCheck,
  Coins,
} from 'lucide-react';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import useAppVersion from '../../hooks/useAppVersion';

const navItems = [
  {
    to: '/roster',
    icon: Calendar,
    label: 'Roster',
    roles: ['user', 'admin', 'superadmin'],
  },
  {
    to: '/my-shifts',
    icon: CalendarCheck,
    label: 'My Shifts',
    roles: ['user', 'admin', 'superadmin'],
  },
  {
    to: '/availability',
    icon: Clock,
    label: 'Availability',
    roles: ['user', 'admin', 'superadmin'],
    requiresShifts: true, // admins always see it (team overview)
  },
  {
    to: '/vacation',
    icon: Waves,
    label: 'Vacation',
    roles: ['user', 'admin', 'superadmin'],
  },
  {
    to: '/time-tracking',
    icon: Timer,
    label: 'Time Tracking',
    roles: ['user', 'admin', 'superadmin'],
    // Sub-items shown only to holders of at least one active OM role
    children: [
      { to: '/om-time', icon: Clock, label: 'Amtszeit', requiresOmRole: true },
    ],
  },
  {
    to: '/om-roles',
    icon: ClipboardList,
    label: 'OM-Ämter',
    roles: ['admin', 'superadmin'],
  },
  {
    to: '/shift-templates',
    icon: LayoutList,
    label: 'Shift Templates',
    roles: ['admin', 'superadmin'],
  },
  {
    to: '/users',
    icon: Users,
    label: 'Users',
    roles: ['admin', 'superadmin'],
  },
  {
    to: '/reports',
    icon: BarChart2,
    label: 'Reports',
    roles: ['admin', 'superadmin'],
  },
  {
    to: '/tips',
    icon: Coins,
    label: 'Tips',
    roles: ['user', 'admin', 'superadmin'],
    requiresTips: true, // admins always; staff with the "Tip calculator" permission
  },
  {
    to: '/sick-notes',
    icon: FileCheck,
    label: 'Sick Notes',
    roles: ['admin', 'superadmin'],
  },
  {
    to: '/notifications',
    icon: Bell,
    label: 'Notifications',
    roles: ['user', 'admin', 'superadmin'],
  },
  {
    to: '/profile',
    icon: User,
    label: 'Profile',
    roles: ['user', 'admin', 'superadmin'],
  },
  {
    to: '/admin',
    icon: ShieldAlert,
    label: 'Admin Panel',
    roles: ['admin', 'superadmin'],
  },
];

function Sidebar({ isOpen, onClose }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { label: versionLabel, built: buildTime } = useAppVersion();
  const [showBuild, setShowBuild] = useState(false); // tap on mobile

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose?.();
  };

  const location = useLocation();

  // Does the user hold at least one active OM role? (same rule as the Amtszeit page)
  const [hasOmRole, setHasOmRole] = useState(false);
  useEffect(() => {
    if (!user?.id) { setHasOmRole(false); return; }
    let cancelled = false;
    api.get('/om/roles')
      .then((res) => {
        if (cancelled) return;
        setHasOmRole((res.data || []).some(
          (r) => r.is_active && String(r.holder_user_id) === String(user.id)
        ));
      })
      .catch(() => { if (!cancelled) setHasOmRole(false); });
    return () => { cancelled = true; };
  }, [user?.id]);

  const filteredItems = navItems
    .filter((item) =>
      item.roles.includes(user?.role) &&
      (!item.requiresShifts || isAdmin || user?.works_shifts !== 0) &&
      (!item.requiresTips || isAdmin || user?.can_use_tips === 1)
    )
    .map((item) => ({
      ...item,
      children: (item.children || []).filter((c) => !c.requiresOmRole || hasOmRole),
    }));

  // Expanded groups; a group auto-expands while one of its pages is active
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    for (const item of navItems) {
      if ((item.children || []).some((c) => location.pathname.startsWith(c.to))) {
        setExpanded((prev) => (prev[item.to] ? prev : { ...prev, [item.to]: true }));
      }
    }
  }, [location.pathname]);
  const toggleGroup = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const linkStyle = (isSub) => ({ isActive }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: isSub ? '8px 12px 8px 22px' : '10px 12px',
    minHeight: '40px',
    borderRadius: 'var(--radius)',
    textDecoration: 'none',
    fontSize: isSub ? '0.86rem' : '0.9rem',
    fontWeight: isActive ? 600 : 400,
    color: isActive ? 'var(--primary-dark)' : 'var(--text)',
    background: isActive ? 'var(--primary-xlight)' : 'transparent',
    borderLeft: isActive ? '3px solid var(--primary-dark)' : '3px solid transparent',
    transition: 'var(--transition)',
    flex: 1,
    minWidth: 0,
  });
  const hoverOn = (e) => {
    if (!e.currentTarget.style.background.includes('xlight')) e.currentTarget.style.background = 'var(--bg)';
  };
  const hoverOff = (e) => {
    if (!e.currentTarget.style.background.includes('xlight')) e.currentTarget.style.background = 'transparent';
  };

  return (
    <>
      {/* Overlay on mobile */}
      {isOpen && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 49,
            display: 'none',
          }}
          className="sidebar-overlay"
        />
      )}

      <aside
        style={{
          position: 'fixed',
          top: 'var(--navbar-height)',
          left: isOpen ? 0 : 'calc(-1 * var(--sidebar-width))',
          width: 'var(--sidebar-width)',
          height: 'calc(100vh - var(--navbar-height))',
          background: 'var(--white)',
          borderRight: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 50,
          transition: 'left 0.25s ease',
          overflowY: 'auto',
          boxShadow: isOpen ? '2px 0 12px rgba(0,0,0,0.08)' : 'none',
        }}
      >
        <nav style={{ flex: 1, padding: '12px 8px' }}>
          <ul>
            {filteredItems.map((item) => {
              const hasChildren = item.children.length > 0;
              const isOpen = !!expanded[item.to];
              return (
                <li key={item.to} style={{ marginBottom: '2px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <NavLink
                      to={item.to}
                      end={hasChildren}
                      onClick={onClose}
                      style={linkStyle(false)}
                      onMouseEnter={hoverOn}
                      onMouseLeave={hoverOff}
                    >
                      <item.icon size={18} strokeWidth={2} />
                      {item.label}
                    </NavLink>
                    {hasChildren && (
                      <button
                        type="button"
                        onClick={() => toggleGroup(item.to)}
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${item.label}`}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: '36px', height: '40px', flexShrink: 0,
                          background: 'none', border: 'none', borderRadius: 'var(--radius)',
                          cursor: 'pointer', color: 'var(--text-light)',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                      >
                        <ChevronDown
                          size={16}
                          style={{ transition: 'transform 0.2s ease', transform: isOpen ? 'rotate(180deg)' : 'none' }}
                        />
                      </button>
                    )}
                  </div>
                  {hasChildren && isOpen && (
                    <ul style={{ margin: '2px 0 4px 14px', paddingLeft: '4px', borderLeft: '1px solid var(--primary-light)' }}>
                      {item.children.map((child) => (
                        <li key={child.to} style={{ marginBottom: '2px', display: 'flex' }}>
                          <NavLink
                            to={child.to}
                            onClick={onClose}
                            style={linkStyle(true)}
                            onMouseEnter={hoverOn}
                            onMouseLeave={hoverOff}
                          >
                            <child.icon size={16} strokeWidth={2} />
                            {child.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom: user info + logout */}
        <div
          style={{
            padding: '12px',
            borderTop: '1px solid var(--border)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 12px',
              background: 'var(--bg)',
              borderRadius: 'var(--radius)',
              marginBottom: '8px',
            }}
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'var(--primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '0.85rem',
                color: 'var(--text)',
                flexShrink: 0,
              }}
            >
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.name}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-light)', textTransform: 'capitalize' }}>
                {user?.role}
              </div>
            </div>
          </div>

          <button
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
              padding: '9px 12px',
              borderRadius: 'var(--radius)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.9rem',
              color: 'var(--danger)',
              transition: 'var(--transition)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--danger-light)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
          >
            <LogOut size={17} />
            Sign out
          </button>

          {isAdmin && versionLabel && (
            <div
              title={buildTime ? `Built on server: ${buildTime}` : undefined}
              onClick={() => buildTime && setShowBuild((s) => !s)}
              style={{
                marginTop: '8px', padding: '4px 12px', fontSize: '0.68rem', color: 'var(--text-muted)', lineHeight: 1.4,
                cursor: buildTime ? 'pointer' : 'default',
              }}
            >
              {versionLabel}
              {showBuild && buildTime && <div>Built on server: {buildTime}</div>}
            </div>
          )}
        </div>
      </aside>

      <style>{`
        @media (max-width: 768px) {
          .sidebar-overlay {
            display: block !important;
          }
        }
      `}</style>
    </>
  );
}

export default Sidebar;
